// src/schedule/SchedulePage.js
import React, { useState, useRef, useEffect, useMemo, useCallback } from "react";
import { createPortal } from "react-dom";
import ScheduleDays from "./ScheduleDays";
import EmployeeViewDays from "./EmployeeViewDays";
import MobileFilterFab from "./MobileFilterFab";
import DndProvider from "./DndProvider";
import { createOnDragEnd } from "./dndOnDragEnd";

import { useColumnAssignments } from "../hooks/useColumnAssignments";
import { useScheduleFilters, useEventLists } from "../hooks/useScheduleFilters";
import StaffConflictPromptHost from "./components/StaffConflictPromptHost";
import { isPermanentEmployee, listTemporaryEmployees, upsertTemporaryInList } from "../utils/employees";
// Helpers
const asName = (x) => (typeof x === "string" ? x : x?.name || "");
const isRealEvent = (e) => !!e && !e._isFreeDay && !!e.event;
const norm = (s) => String(s || "").trim().toLowerCase();

// Nieznany / niepewny przydział (TBD, "?", "(?)", obiekt placeholder)
const isUnknownAssignee = (s) => {
    try {
        if (!s) return true;
        if (typeof s === "object" && (s.unknown || s.tentative || s.isPlaceholder)) return true;

        const rawStr =
            typeof s === "string"
                ? s
                : typeof s?.name === "string"
                    ? s.name
                    : typeof s?.label === "string"
                        ? s.label
                        : "";

        const raw = String(rawStr || "").trim();
        if (!raw) return true;

        const lower = raw.toLowerCase();
        if (lower === "tbd" || lower === "?" || lower === "brak" || lower === "n/a") return true;

        if (/\(\s*\?\s*\)/.test(raw)) return true; // "Mateusz Kwella (?)"
        if (/^\?\s*/.test(raw)) return true; // "? Kowalski"
        if (/\s*\?$/.test(raw)) return true; // "Kowalski ?"
        if (raw.includes("?")) return true;

        return false;
    } catch {
        return false;
    }
};

export default function SchedulePage(props) {
    const {
        isSyncing = false, // Wskaźnik synchronizacji w tle
        // dane/akcje z rodzica (niektóre mogą być niepodane)
        eventsByDate,
        activeMonthKey,
        setActiveMonthKey,
        grouped,
        months,
        updateEventStaff,
        updateEventColor,
        employees,
        isAdmin,
        unavailableByDate,
        toggleUnavailable,
        updateNeededForEvent,
        copyStaffToAllDates,
        selectedCalendar,
        theatreCalendar,
        setEventsByDate,
        setEmployees,
        setUnavailableByDate,
        setSelectedCalendar,
        setTheatreCalendar,
        loading = false,
        dataReady = false, // rodzic może tego nie przekazać
    } = props;

    // ───────────────────
    // Fallback state (gdy rodzic nie przekaże setterów)
    // ───────────────────
    const [internalEventsByDate, setInternalEventsByDate] = useState({});
    const [internalEmployees, setInternalEmployees] = useState([]);
    const [internalUnavailableByDate, setInternalUnavailableByDate] = useState({});
    const [internalSelectedCalendar, setInternalSelectedCalendar] = useState(null);
    const [internalTheatreCalendar, setInternalTheatreCalendar] = useState(null);

    // Efektywne źródła danych (props lub fallback)
    const eventsByDateSrc = eventsByDate ?? internalEventsByDate;
    const employeesSrc = employees ?? internalEmployees;
    const unavailableByDateSrc = unavailableByDate ?? internalUnavailableByDate;
    const selectedCalendarSrc = selectedCalendar ?? internalSelectedCalendar;
    const theatreCalendarSrc = theatreCalendar ?? internalTheatreCalendar;

    // Cache widoku (domyślnie „main")
    const [currentView, setCurrentView] = useState(() => {
        try {
            return localStorage.getItem("planner:currentView") || "main";
        } catch {
            return "main";
        }
    });
    useEffect(() => {
        try {
            localStorage.setItem("planner:currentView", currentView);
        } catch { }
    }, [currentView]);

    const [tbdOnly, setTbdOnly] = useState(false);
    const [selectedEmployee, setSelectedEmployee] = useState("");
    const [showColorPicker, setShowColorPicker] = useState(null);
    const [isMobile, setIsMobile] = useState(false);
    const [filtersOpen, setFiltersOpen] = useState(false);
    const [showGoogleCalendar, setShowGoogleCalendar] = useState(false);
    const [selectedEventNames, setSelectedEventNames] = useState([]);
    const [eventSearchQuery, setEventSearchQuery] = useState("");
    const [selectedEventName, setSelectedEventName] = useState("");
    // Mobile: który dzień jest aktualnie powiększony (ISO YYYY-MM-DD)
    const [expandedDay, setExpandedDay] = useState(null);
    const [closingDay, setClosingDay] = useState(null);
    const closingTimerRef = useRef(null);

    const todayStr = useMemo(() => {
        const now = new Date();
        const local = new Date(now.getTime() - now.getTimezoneOffset() * 60000);
        return local.toISOString().slice(0, 10);
    }, []);

    // Ustaw bieżący miesiąc na starcie; pełne zależności + strażnik, żeby nie zapętlać
    const initMonthDoneRef = useRef(false);
    useEffect(() => {
        if (initMonthDoneRef.current) return;
        initMonthDoneRef.current = true;
        const desired = todayStr.slice(0, 7); // "YYYY-MM"
        if (typeof setActiveMonthKey === "function") {
            const current = String(activeMonthKey || "");
            if (current.slice(0, 7) !== desired) {
                setActiveMonthKey(desired);
            }
        }
    }, [activeMonthKey, setActiveMonthKey, todayStr]);

    // Normalizacja "YYYY-M" -> "YYYY-MM"
    useEffect(() => {
        if (!activeMonthKey || typeof setActiveMonthKey !== "function") return;
        const m = String(activeMonthKey);
        if (/^\d{4}-\d$/.test(m)) {
            const [y, mm] = m.split("-");
            setActiveMonthKey(`${y}-${String(mm).padStart(2, "0")}`);
        }
    }, [activeMonthKey, setActiveMonthKey]);

    // Mobile detect
    useEffect(() => {
        const onResize = () => setIsMobile(window.innerWidth <= 768);
        onResize();
        window.addEventListener("resize", onResize);
        return () => window.removeEventListener("resize", onResize);
    }, []);

    const cancelClosingTimeout = useCallback(() => {
        if (closingTimerRef.current) {
            clearTimeout(closingTimerRef.current);
            closingTimerRef.current = null;
        }
    }, []);

    const startClosingExpandedDay = useCallback(() => {
        if (!expandedDay) return;
        cancelClosingTimeout();
        setClosingDay(expandedDay);
        closingTimerRef.current = setTimeout(() => {
            setExpandedDay(null);
            setClosingDay(null);
            closingTimerRef.current = null;
        }, 250);
    }, [expandedDay, cancelClosingTimeout]);

    useEffect(() => {
        return () => cancelClosingTimeout();
    }, [cancelClosingTimeout]);

    const handleDayClick = (date) => {
        if (!isMobile) return;
        if (expandedDay === date) {
            startClosingExpandedDay();
        } else {
            cancelClosingTimeout();
            setClosingDay(null);
            setExpandedDay(date);
        }
    };

    // Blokuj scroll strony gdy karta jest otwarta
    useEffect(() => {
        if (isMobile && expandedDay) {
            // Zapisz aktualną pozycję scrolla
            const scrollY = window.scrollY;
            // Zablokuj scroll przez dodanie klasy
            document.body.classList.add('day-card-expanded-open');
            document.body.style.top = `-${scrollY}px`;
            
            return () => {
                // Przywróć scroll
                document.body.classList.remove('day-card-expanded-open');
                document.body.style.top = '';
                window.scrollTo(0, scrollY);
            };
        }
    }, [isMobile, expandedDay]);


    // Zamykanie powiększenia klawiszem ESC
    useEffect(() => {
        if (!isMobile || !expandedDay) return;
        
        const handleEscape = (e) => {
            if (e.key === 'Escape') {
                startClosingExpandedDay();
            }
        };
        
        document.addEventListener('keydown', handleEscape);
        return () => document.removeEventListener('keydown', handleEscape);
    }, [isMobile, expandedDay, startClosingExpandedDay]);

    const rowRefs = useRef({});
    // Employees list (lint-safe)
    const employeesToUse = useMemo(() => {
        const fullEmployeeList = (employeesSrc || []).filter(isPermanentEmployee);
        if (currentView === "theatre") {
            const anyHasFlag = fullEmployeeList.some(
                (e) => typeof e === "object" && "isTheatre" in e
            );
            return anyHasFlag
                ? fullEmployeeList.filter((e) => (e && typeof e === "object" ? e.isTheatre : false))
                : fullEmployeeList;
        }
        return fullEmployeeList;
    }, [employeesSrc, currentView]);

    const temporaryEmployees = useMemo(
        () => listTemporaryEmployees(employeesSrc || []),
        [employeesSrc]
    );

    const handleCreateTemporary = useCallback((fields) => {
        const updateEmployees = setEmployees || setInternalEmployees;
        if (typeof updateEmployees !== "function") {
            return { error: "Brak uprawnień do dodawania osób tymczasowych" };
        }
        const result = upsertTemporaryInList(employeesSrc || [], fields);
        if (result.error || !result.employee) {
            return { error: result.error || "Nie udało się dodać osoby tymczasowej" };
        }
        updateEmployees(result.employees);
        return { employee: result.employee };
    }, [employeesSrc, setEmployees, setInternalEmployees]);

    const activeCalendarId = useMemo(() => {
        const result = currentView === "main" ? selectedCalendarSrc : (currentView === "theatre" ? theatreCalendarSrc : undefined);
        console.log(`[SchedulePage] activeCalendarId dla widoku "${currentView}":`, {
            currentView,
            selectedCalendarSrc,
            theatreCalendarSrc,
            activeCalendarId: result
        });
        return result;
    }, [currentView, selectedCalendarSrc, theatreCalendarSrc]);

    // Dla DnD: w widoku "all" nie przepychamy calendarId – handler wyciągnie z eventu
    const dndCalendarId = currentView === "all" ? null : activeCalendarId || null;

    // Dopóki ID kalendarza nieznane → nie filtrujemy po nim (jak "all")
    const matchesView = useMemo(() => {
        const allowed = new Set(
            [selectedCalendarSrc, theatreCalendarSrc].filter(Boolean)
        );
        return (ev) => {
            const evCal = ev?.calendarId || "";

            if (currentView === "all") {
                // Ręczne eventy tylko w ALL
                if (!evCal) return true;
                if (allowed.size === 0) return true;
                return allowed.has(evCal);
            }

            if (currentView === "theatre") {
                if (!theatreCalendarSrc) return false;
                return evCal === theatreCalendarSrc;
            }

            if (currentView === "main") {
                if (!selectedCalendarSrc) return false;
                return evCal === selectedCalendarSrc;
            }

            return true;
        };
    }, [currentView, selectedCalendarSrc, theatreCalendarSrc]);

    // Daty miesiąca (1-based w activeMonthKey)
    const monthDatesUTC = (key) => {
        if (!key) return [];
        const [yStr, mStr] = String(key).split("-");
        const y = Number(yStr);
        const m0 = Number(mStr) - 1; // 0-based
        const daysInMonth = new Date(y, m0 + 1, 0).getDate();
        const out = [];
        for (let day = 1; day <= daysInMonth; day++) {
            const d = new Date(Date.UTC(y, m0, day, 0, 0, 0));
            out.push(d.toISOString().slice(0, 10));
        }
        return out;
    };

    const monthDatesFull = useMemo(() => monthDatesUTC(activeMonthKey), [activeMonthKey]);

    // Funkcje nawigacji między dniami (po definicji monthDatesFull)
    const handlePreviousDay = useCallback(() => {
        if (!expandedDay) return;
        const currentIndex = monthDatesFull.indexOf(expandedDay);
        if (currentIndex > 0) {
            cancelClosingTimeout();
            setClosingDay(null);
            setExpandedDay(monthDatesFull[currentIndex - 1]);
        }
    }, [expandedDay, monthDatesFull, cancelClosingTimeout]);

    const handleNextDay = useCallback(() => {
        if (!expandedDay) return;
        const currentIndex = monthDatesFull.indexOf(expandedDay);
        if (currentIndex < monthDatesFull.length - 1) {
            cancelClosingTimeout();
            setClosingDay(null);
            setExpandedDay(monthDatesFull[currentIndex + 1]);
        }
    }, [expandedDay, monthDatesFull, cancelClosingTimeout]);

    const canGoPrevious = useMemo(() => expandedDay && monthDatesFull.indexOf(expandedDay) > 0, [expandedDay, monthDatesFull]);
    const canGoNext = useMemo(() => expandedDay && monthDatesFull.indexOf(expandedDay) < monthDatesFull.length - 1, [expandedDay, monthDatesFull]);

    // Filtrowanie eventów
    const { eventsForViewByDate } = useScheduleFilters({
        eventsByDate: eventsByDateSrc,
        monthDates: monthDatesFull,
        matchesView,
        selectedEmployee,
        tbdOnly: tbdOnly === true,
        selectedEventNames,
        eventSearchQuery, // Dodajemy wyszukiwanie tekstowe
    });

    const eventsByDateIso = eventsForViewByDate;
    const datesForRender = useMemo(() => monthDatesFull, [monthDatesFull]);

    const datesWithEvents = useMemo(
        () => datesForRender.filter((d) => (eventsForViewByDate[d] || []).length > 0),
        [datesForRender, eventsForViewByDate]
    );

    const nonEmptyEventsByDate = useMemo(
        () =>
            Object.fromEntries(
                Object.entries(eventsForViewByDate).filter(([, evs]) => (evs || []).length > 0)
            ),
        [eventsForViewByDate]
    );

    // Przypisywanie kolumn
    const columnAssignments = useColumnAssignments(datesWithEvents, nonEmptyEventsByDate);

    // DnD i utilsy
    const getAllEventsForDate = (date) => (eventsByDateIso?.[date] || []).filter(isRealEvent);

    const getAssignedNamesForDate = (date) => {
        const evs = eventsByDateIso?.[date] || [];
        const assigned = new Set();
        evs.forEach((ev) => {
            (ev.staff || []).forEach((s) => {
                const name = asName(s);
                if (name) assigned.add(name);
            });
        });
        return assigned;
    };

    const getUnavailableNamesForDate = (date) =>
        new Set((unavailableByDateSrc?.[date] || []).map((e) => asName(e)));

    const onDragEnd = useMemo(
        () =>
            createOnDragEnd({
                eventsByDate: eventsByDateIso || {},
                updateEventStaff,
                updateNeededForEvent,
                activeCalendarId: currentView === "all" ? null : (activeCalendarId || null),
            }),
        [eventsByDateIso, updateEventStaff, updateNeededForEvent, currentView, activeCalendarId]
    );

    // ————————————————————————————————————————————
    //            SCROLL DO „DZIŚ”
    // ————————————————————————————————————————————
    const prevLoadingRef = useRef(true);
    const didScrollForMonthRef = useRef(null);

    const sameMonth = (monthKey, iso) => {
        if (!monthKey || !iso) return false;
        const mk = String(monthKey).replace(/-(\d)$/, "-0$1");
        return mk === String(iso).slice(0, 7);
    };

    const scrollToIsoDate = (iso) => {
        if (!iso) return;
        let target =
            rowRefs.current?.[iso] ||
            document.querySelector(`[data-day-anchor="${iso}"]`) ||
            document.getElementById(`day-${iso}`);
        if (!target) return;
        const top = target.getBoundingClientRect().top + window.scrollY - 40;
        window.scrollTo({ top, behavior: "smooth" });
    };

    // Automatyczne przewijanie do dzisiaj przy załadowaniu/odświeżeniu
    useEffect(() => {
        const becameReady = prevLoadingRef.current && !loading;
        prevLoadingRef.current = loading;
        
        // Automatycznie przewiń do dzisiaj tylko przy pierwszym załadowaniu
        if (becameReady && didScrollForMonthRef.current === null) {
            // Upewnij się, że miesiąc jest ustawiony na dzisiejszy
            const todayMonth = todayStr.slice(0, 7);
            if (activeMonthKey !== todayMonth && typeof setActiveMonthKey === "function") {
                setActiveMonthKey(todayMonth);
                // Poczekaj na render, potem przewiń
                setTimeout(() => {
                    scrollToIsoDate(todayStr);
                    didScrollForMonthRef.current = todayMonth;
                }, 200);
            } else {
                // Jeśli już jesteśmy w dzisiejszym miesiącu, przewiń od razu
                requestAnimationFrame(() =>
                    setTimeout(() => {
                        scrollToIsoDate(todayStr);
                        didScrollForMonthRef.current = todayMonth;
                    }, 100)
                );
            }
        }
    }, [loading, todayStr, activeMonthKey, setActiveMonthKey]);

    // Listy do wyszukiwania wydarzeń
    const { eventsAllNames, tbdEventNames, eventsForSearch } = useEventLists({
        eventsByDate: eventsByDateSrc,
        monthDates: monthDatesFull,
        matchesView,
        tbdOnly,
    });

    // Flags do overlay/loadera
    const eventsKeysCount = useMemo(() => Object.keys(eventsByDateSrc || {}).length, [eventsByDateSrc]);
    const hasAnyData = eventsKeysCount > 0;

    return (
        <div className="p-3 md:p-4">
            {/* DUŻY OVERLAY tylko gdy ładujemy i NIE mamy jeszcze żadnych danych */}
            {!hasAnyData && loading && (
                <div className="fixed inset-0 z-[10000] flex items-center justify-center
                        bg-white text-gray-900 dark:bg-gray-900 dark:text-gray-100">
                    <div className="flex flex-col items-center gap-4">
                        <img
                            src="/icons/Logo-192.png"
                            alt="Logo"
                            className="w-20 h-20 md:w-24 md:h-24"
                        />
                        <div
                            className="h-8 w-8 rounded-full border-4 border-gray-300 border-t-transparent
                         dark:border-gray-600 dark:border-t-transparent animate-spin"
                        />
                    </div>
                </div>
            )}

            {/* MAŁY KRĘCIOŁEK W ROGU — gdy dociągamy dane */}
            {(loading || isSyncing) && hasAnyData && (
                <div className="fixed top-3 right-3 z-[9999] pointer-events-none">
                    <div
                        title={isSyncing ? "Synchronizacja w tle…" : "Ładowanie…"}
                        className="h-5 w-5 rounded-full border-2 border-gray-300 border-t-transparent
                       dark:border-gray-600 dark:border-t-transparent animate-spin"
                    />
                </div>
            )}


            <MobileFilterFab
                open={filtersOpen}
                onToggle={() => setFiltersOpen((p) => !p)}
                activeMonthKey={activeMonthKey}
                setActiveMonthKey={setActiveMonthKey}
                months={
                    months || [
                        "Styczeń", "Luty", "Marzec", "Kwiecień", "Maj", "Czerwiec",
                        "Lipiec", "Sierpień", "Wrzesień", "Październik", "Listopad", "Grudzień",
                    ]
                }
                currentView={currentView}
                setCurrentView={setCurrentView}
                employees={employeesToUse}
                selectedEmployee={selectedEmployee}
                setSelectedEmployee={setSelectedEmployee}
                tbdOnly={tbdOnly}
                setTbdOnly={setTbdOnly}
                showGoogleCalendar={showGoogleCalendar}
                setShowGoogleCalendar={setShowGoogleCalendar}
                eventSearchQuery={eventSearchQuery}
                onChangeEventSearchQuery={setEventSearchQuery}
            />

            <div
                className={isMobile ? "mobile-zoom-container" : ""}
                style={isMobile ? { "--mobile-zoom": 1 } : undefined}
            >
                <DndProvider onDragEnd={onDragEnd}>
                    {selectedEmployee && selectedEmployee !== "TBD" ? (
                        <EmployeeViewDays
                            filteredDates={monthDatesFull}
                            todayStr={todayStr}
                            eventsByDate={eventsByDateIso || {}}
                            selectedEmployee={selectedEmployee}
                            employees={employeesToUse}
                            temporaryEmployees={temporaryEmployees}
                            onCreateTemporary={isAdmin ? handleCreateTemporary : undefined}
                            updateEventStaff={(...args) => {
                                const [date, eventName, newStaff, calendarId, save] = args;
                                const cal = calendarId !== undefined ? calendarId : activeCalendarId;
                                return updateEventStaff(date, eventName, newStaff, cal, save ?? true);
                            }}
                            updateEventColor={updateEventColor}
                            unavailableByDate={unavailableByDateSrc}
                            toggleUnavailable={toggleUnavailable}
                            isAdmin={isAdmin}
                            copyStaffToAllDates={copyStaffToAllDates}
                            updateNeededForEvent={updateNeededForEvent}
                            getAssignedNamesForDate={getAssignedNamesForDate}
                            getUnavailableNamesForDate={getUnavailableNamesForDate}
                            activeCalendarId={activeCalendarId}
                            allEventsForDate={(d) => (eventsByDateIso?.[d] || []).filter(isRealEvent)}
                            eventsByDateFull={eventsByDateSrc}
                            rowRefs={rowRefs}
                            dragScale={1}
                            expandedDay={expandedDay}
                            onDayClick={handleDayClick}
                        />
                    ) : (
                        <ScheduleDays
                            filteredDates={monthDatesFull}
                            todayStr={todayStr}
                            eventsByDate={eventsForViewByDate}
                            employees={employeesToUse}
                            temporaryEmployees={temporaryEmployees}
                            onCreateTemporary={isAdmin ? handleCreateTemporary : undefined}
                            updateEventStaff={(...args) => {
                                const [date, eventName, newStaff, calendarId, save] = args;
                                const cal = calendarId !== undefined ? calendarId : activeCalendarId;
                                return updateEventStaff(date, eventName, newStaff, cal, save ?? true);
                            }}
                            updateEventColor={updateEventColor}
                            columnAssignments={columnAssignments}
                            showColorPicker={showColorPicker}
                            setShowColorPicker={setShowColorPicker}
                            unavailableByDate={unavailableByDateSrc}
                            toggleUnavailable={toggleUnavailable}
                            maxWidth={0}
                            isAdmin={isAdmin}
                            copyStaffToAllDates={copyStaffToAllDates}
                            updateNeededForEvent={updateNeededForEvent}
                            rowRefs={rowRefs}
                            getAssignedNamesForDate={getAssignedNamesForDate}
                            getUnavailableNamesForDate={getUnavailableNamesForDate}
                            activeCalendarId={activeCalendarId}
                            allEventsForDate={(d) => (eventsByDateIso?.[d] || []).filter(isRealEvent)}
                            eventsByDateFull={eventsByDateSrc}
                            renderGapsBetweenDatesAsFree={true}
                            renderFreeForEmpty={true}
                            expandedDay={expandedDay}
                            onDayClick={handleDayClick}
                        />
                    )}
                </DndProvider>
            </div>

            {/* Powiększona karta w portalu - renderowana poza kontenerem z transform */}
            {isMobile && expandedDay && typeof document !== 'undefined' && createPortal(
                <div 
                    className={`day-card-expanded-portal${closingDay && closingDay === expandedDay ? ' day-card-expanded-portal--closing' : ''}`}
                    onMouseMove={(e) => {
                        // Przechwyć pozycję kursora dla DnD
                        window.lastMouseEvent = e;
                    }}
                    onTouchMove={(e) => {
                        if (e.touches.length > 0) {
                            window.lastMouseEvent = {
                                clientX: e.touches[0].clientX,
                                clientY: e.touches[0].clientY
                            };
                        }
                    }}
                    onClick={(e) => {
                        // Zatrzymaj propagację kliknięć wewnątrz karty
                        // Ale nie blokuj drag & drop - sprawdź czy to nie jest element drag
                        const isDragElement = e.target.closest('.staff-item') || 
                                             e.target.closest('[data-rbd-drag-handle-draggable-id]') ||
                                             e.target.closest('[data-rbd-draggable-id]');
                        if (!isDragElement) {
                            e.stopPropagation();
                        }
                    }}
                    style={{ display: 'flex', flexDirection: 'column', height: '100%' }}
                >
                    <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', overflowX: 'hidden' }}>
                        <DndProvider onDragEnd={onDragEnd}>
                        {selectedEmployee && selectedEmployee !== "TBD" ? (
                            <EmployeeViewDays
                                filteredDates={[expandedDay]}
                                todayStr={todayStr}
                                eventsByDate={eventsByDateIso || {}}
                                selectedEmployee={selectedEmployee}
                                employees={employeesToUse}
                                temporaryEmployees={temporaryEmployees}
                                onCreateTemporary={isAdmin ? handleCreateTemporary : undefined}
                                updateEventStaff={(...args) => {
                                    const [date, eventName, newStaff, calendarId, save] = args;
                                    const cal = calendarId !== undefined ? calendarId : activeCalendarId;
                                    return updateEventStaff(date, eventName, newStaff, cal, save ?? true);
                                }}
                                updateEventColor={updateEventColor}
                                unavailableByDate={unavailableByDateSrc}
                                toggleUnavailable={toggleUnavailable}
                                isAdmin={isAdmin}
                                copyStaffToAllDates={copyStaffToAllDates}
                                updateNeededForEvent={updateNeededForEvent}
                                getAssignedNamesForDate={getAssignedNamesForDate}
                                getUnavailableNamesForDate={getUnavailableNamesForDate}
                                activeCalendarId={activeCalendarId}
                                allEventsForDate={(d) => (eventsByDateIso?.[d] || []).filter(isRealEvent)}
                                eventsByDateFull={eventsByDateSrc}
                                rowRefs={rowRefs}
                                dragScale={1}
                                expandedDay={expandedDay}
                                onDayClick={handleDayClick}
                                onCloseExpanded={startClosingExpandedDay}
                                isPortalView={true}
                            />
                        ) : (
                            <ScheduleDays
                                filteredDates={[expandedDay]}
                                todayStr={todayStr}
                                eventsByDate={eventsForViewByDate}
                                employees={employeesToUse}
                                temporaryEmployees={temporaryEmployees}
                                onCreateTemporary={isAdmin ? handleCreateTemporary : undefined}
                                updateEventStaff={(...args) => {
                                    const [date, eventName, newStaff, calendarId, save] = args;
                                    const cal = calendarId !== undefined ? calendarId : activeCalendarId;
                                    return updateEventStaff(date, eventName, newStaff, cal, save ?? true);
                                }}
                                updateEventColor={updateEventColor}
                                columnAssignments={columnAssignments}
                                showColorPicker={showColorPicker}
                                setShowColorPicker={setShowColorPicker}
                                unavailableByDate={unavailableByDateSrc}
                                toggleUnavailable={toggleUnavailable}
                                maxWidth={0}
                                isAdmin={isAdmin}
                                copyStaffToAllDates={copyStaffToAllDates}
                                updateNeededForEvent={updateNeededForEvent}
                                rowRefs={rowRefs}
                                getAssignedNamesForDate={getAssignedNamesForDate}
                                getUnavailableNamesForDate={getUnavailableNamesForDate}
                                activeCalendarId={activeCalendarId}
                                allEventsForDate={(d) => (eventsByDateIso?.[d] || []).filter(isRealEvent)}
                                eventsByDateFull={eventsByDateSrc}
                                renderGapsBetweenDatesAsFree={false}
                                renderFreeForEmpty={false}
                                expandedDay={expandedDay}
                                onDayClick={handleDayClick}
                                onCloseExpanded={startClosingExpandedDay}
                                isPortalView={true}
                            />
                        )}
                    </DndProvider>
                    </div>
                    {/* Strzałki nawigacyjne i przycisk X - bezpośrednio pod oknem powiększenia */}
                    <div 
                        className="day-card-expanded-buttons"
                        style={{
                            display: 'flex',
                            flexDirection: 'row',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            padding: '0.75rem 1rem',
                            width: '100%',
                            flexShrink: 0,
                            borderTop: '1px solid rgba(255,255,255,0.1)',
                            backgroundColor: 'rgba(31, 41, 55, 0.95)',
                            zIndex: 10
                        }}
                    >
                        <button
                            onClick={handlePreviousDay}
                            disabled={!canGoPrevious}
                            className={`px-4 py-2 rounded text-lg ${canGoPrevious ? 'bg-gray-700 hover:bg-gray-600' : 'bg-gray-800 opacity-50 cursor-not-allowed'}`}
                            title="Poprzedni dzień"
                        >
                            ←
                        </button>
                        <button
                            onClick={startClosingExpandedDay}
                            className="px-4 py-2 rounded text-lg bg-gray-700 hover:bg-gray-600"
                            title="Zamknij"
                        >
                            ✕
                        </button>
                        <button
                            onClick={handleNextDay}
                            disabled={!canGoNext}
                            className={`px-4 py-2 rounded text-lg ${canGoNext ? 'bg-gray-700 hover:bg-gray-600' : 'bg-gray-800 opacity-50 cursor-not-allowed'}`}
                            title="Następny dzień"
                        >
                            →
                        </button>
                    </div>
                </div>,
                document.body
            )}

            <StaffConflictPromptHost />

            {showGoogleCalendar && (
                <div className="mt-4 border border-gray-700 rounded-lg overflow-hidden">
                    <iframe
                        src="https://calendar.google.com/calendar/embed?src=712ijfdfion8cfa72o0sdberl8%40group.calendar.google.com&ctz=Europe%2FWarsaw"
                        style={{ border: 0 }}
                        width="100%"
                        height="600"
                        title="Google Calendar Preview"
                    />
                </div>
            )}
        </div>
    );
}
